'use client';

import type {
  CSSObjectWithLabel,
  SingleValue,
  StylesConfig,
} from 'react-select';
import { useCallback, useEffect, useMemo, useState } from 'react';

export type CSSVariables = Record<string, string[]>;

type Option<T> = { label: string; value: T };

const useSelect = <T>(
  cssVariables: CSSVariables,
  role: string,
  disabled: boolean,
  optionSelected: (value?: T) => void,
  border: boolean = false
) => {
  const [bodyElement, setBodyElement] = useState<HTMLElement | null>(null);

  const onChange = useCallback(
    (option: SingleValue<Option<T>>) => {
      if (disabled) return;
      optionSelected(option?.value);
    },
    [optionSelected, disabled]
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setBodyElement(document.body);
  }, []);

  const styles = useMemo<StylesConfig<Option<T>>>(
    () => ({
      option: (base: CSSObjectWithLabel) => {
        const roles: Record<string, CSSObjectWithLabel> = {
          card: {
            backgroundColor: cssVariables.cardBackgroundColor[0],
            '&:active': cssVariables.cardBackgroundColor[0],
            border: 0,
            color: cssVariables.cardSubtitleColor[0],
          },
          input: {
            backgroundColor: cssVariables.inputBackgroundColor[0],
            '&:active': cssVariables.inputBackgroundColor[0],
            border: 0,
            color: cssVariables.inputSelectionTextColor[0],
          },
        };

        return { ...base, ...roles[role] };
      },
      control: (base: CSSObjectWithLabel) => {
        const roles: Record<string, CSSObjectWithLabel> = {
          card: {
            border: `${border ? 1 : 0}px solid ${cssVariables.cardBorderColor[0]}`,
            backgroundColor: cssVariables.cardBackgroundColor[0],
            boxShadow: 'none',
            '&:hover': {
              border: `1px solid ${cssVariables.cardBorderColor[0]}`,
            },
            color: cssVariables.cardSubtitleColor[0],
          },
          input: {
            border: `${border ? 1 : 0}px solid ${cssVariables.inputBorderColor[0]}`,
            backgroundColor: cssVariables.inputBackgroundColor[0],
            boxShadow: 'none',
            '&:hover': {
              border: `1px solid ${cssVariables.inputBorderColor[0]}`,
            },
            color: cssVariables.inputSelectionTextColor[0],
          },
        };

        return { ...base, ...roles[role] };
      },
      menu: (base: CSSObjectWithLabel) => {
        const roles: Record<string, CSSObjectWithLabel> = {
          card: { backgroundColor: cssVariables.cardBackgroundColor[0] },
          input: { backgroundColor: cssVariables.inputBackgroundColor[0] },
        };

        return { ...base, ...roles[role], zIndex: 9999 };
      },
      menuList: (base: CSSObjectWithLabel) => {
        const roles: Record<string, CSSObjectWithLabel> = {
          card: {
            '::-webkit-scrollbar': {
              backgroundColor: cssVariables.cardBackgroundColor[0],
            },
            '::-webkit-scrollbar-track': {
              backgroundColor: cssVariables.cardBackgroundColor[0],
            },
            '::-webkit-scrollbar-thumb': {
              backgroundColor: cssVariables.cardBorderColor[0],
            },
            '::-webkit-scrollbar-thumb:hover': {
              backgroundColor: cssVariables.cardBorderColor[0],
            },
          },
          input: {},
        };

        return { ...base, ...roles[role], borderRadius: '0.25rem' };
      },
      input: (base: CSSObjectWithLabel) => {
        const roles: Record<string, CSSObjectWithLabel> = {
          card: { color: cssVariables.cardSubtitleColor[0] },
          input: { color: cssVariables.inputSelectionTextColor[0] },
        };

        return { ...base, ...roles[role] };
      },
      clearIndicator: (base: CSSObjectWithLabel) => {
        const roles: Record<string, CSSObjectWithLabel> = {
          card: { color: cssVariables.cardSubtitleColor[0] },
          input: { color: cssVariables.inputPlaceholderTextColor[0] },
        };

        return { ...base, ...roles[role] };
      },
      indicatorSeparator: (base: CSSObjectWithLabel) => {
        const roles: Record<string, CSSObjectWithLabel> = {
          card: { backgroundColor: cssVariables.cardBorderColor[0] },
          input: { backgroundColor: cssVariables.inputBorderColor[0] },
        };

        return { ...base, ...roles[role] };
      },
      placeholder: (base: CSSObjectWithLabel) => {
        const roles: Record<string, CSSObjectWithLabel> = {
          card: { color: cssVariables.cardSubtitleColor[0] },
          input: { color: cssVariables.inputPlaceholderTextColor[0] },
        };

        return { ...base, ...roles[role] };
      },
      singleValue: (base: CSSObjectWithLabel) => {
        const roles: Record<string, CSSObjectWithLabel> = {
          card: { color: cssVariables.cardSubtitleColor[0] },
          input: { color: cssVariables.inputSelectionTextColor[0] },
        };

        return { ...base, ...roles[role] };
      },
      dropdownIndicator: (base: CSSObjectWithLabel) => {
        const roles: Record<string, CSSObjectWithLabel> = {
          card: { color: cssVariables.cardSubtitleColor[0] },
          input: { color: cssVariables.inputSelectionTextColor[0] },
        };

        return { ...base, ...roles[role] };
      },
      menuPortal: (base: CSSObjectWithLabel) => ({
        ...base,
        zIndex: 9999,
      }),
    }),
    [cssVariables, role, border]
  );

  return { styles, onChange, bodyElement };
};

export default useSelect;
